import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@mui/material';
import InvButton from '../../../components/InvButton';
import { InvSelect, InvTextField } from '../../../components/InvField';
import { IPD_COLORS, cardSx } from '../../../theme';

/**
 * Reason picker for the order-book terminal transitions (ShortClosed / Cancel).
 *
 * Mirrors the doctor console's cancel dialog (MedicationOrdersPanel.js): a fixed
 * reason list with free text riding on "Other" — because the backend requires a
 * reason for both statuses and 'Other' sends the typed text in its place. One
 * deliberate difference: here the free text is REQUIRED when Other is picked;
 * the IPD panel lets "Other" through empty, which stores the literal word
 * "Other" as the clinical record of why an order died.
 */
export default function OrderReasonDialog({
  open,
  title,
  description,
  reasons = [],
  defaultReason = '',
  confirmLabel = 'Confirm',
  loading = false,
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  // Reset per opening, not on success — a dialog reopened for a different order
  // must not carry the previous order's reason. `defaultReason` seeds it from
  // what the caller already knows (the stock check has established why the line
  // cannot be supplied); it stays editable, because the shelf being empty is not
  // always the reason the pharmacy is declining the line.
  useEffect(() => {
    if (open) {
      setReason(reasons.includes(defaultReason) ? defaultReason : '');
      setNote('');
    }
    // Seeded once per opening: re-seeding on a defaultReason change would undo
    // the pharmacist's pick mid-dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isOther = reason === 'Other';
  const finalReason = isOther ? note.trim() : reason;
  const canConfirm = !!finalReason && !loading;

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose?.()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
            {description}
          </Typography>
        )}
        <InvSelect
          label="Reason *"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={reasons.map((value) => ({ value, label: value }))}
          fullWidth
        />
        {isOther && (
          <InvTextField
            label="Details *"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What actually happened"
            helperText="Sent as the reason — this is the record of why the order was closed."
            fullWidth
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <InvButton variant="outlined" tone="teal" disabled={loading} onClick={onClose}>
          Back
        </InvButton>
        <InvButton loading={loading} disabled={!canConfirm} onClick={() => onConfirm?.(finalReason)}>
          {confirmLabel}
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
